import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Play, Check, RotateCcw, Heart, Tv2 } from "lucide-react";
import { Api, type LibraryItem } from "../lib/api.js";
import { CastStrip } from "../components/CastStrip.js";
import { getActiveProfileId } from "../lib/profile.js";

function useNowPlaying() {
  const q = useQuery({
    queryKey: ["cast-now-playing"],
    queryFn: Api.cast.nowPlaying,
    refetchInterval: 5_000,
    staleTime: 4_000,
  });
  return q.data?.active ? q.data : null;
}

/**
 * Series detail page. Local catalog only — RD-only series open a different
 * detail route. Episodes are grouped by season; each row shows a watch-status
 * marker (✓ done / ↻ in-progress / blank unwatched) and the first unwatched
 * episode is flagged as "Next up" so the viewer always knows where to resume.
 */

interface ParsedTitle {
  season: number | null;
  episode: number | null;
  episodeTitle: string;
}

function parseEpisodeTitle(rawTitle: string, seriesName: string): ParsedTitle {
  const stripped = rawTitle.startsWith(seriesName)
    ? rawTitle.slice(seriesName.length).trim()
    : rawTitle;
  const m = /^S(\d{1,3})\s*E(\d{1,3})(.*)$/i.exec(stripped);
  if (!m) return { season: null, episode: null, episodeTitle: stripped };
  return {
    season: Number(m[1]),
    episode: Number(m[2]),
    episodeTitle: (m[3] ?? "").replace(/^[\s\-—:]+/, "").trim(),
  };
}

type EpisodeRow = LibraryItem & ParsedTitle & {
  tmdb_overview?: string | null;
  tmdb_backdrop_url?: string | null;
  tmdb_rating?: number | null;
  last_position_s?: number | null;
  last_event?: string | null;
  last_seen_at?: string | null;
};

function statusFor(ep: EpisodeRow): "watched" | "in_progress" | "unwatched" {
  if (ep.last_event === "completed") return "watched";
  if (typeof ep.last_position_s === "number" && ep.last_position_s > 30) return "in_progress";
  return "unwatched";
}

export function Series(): JSX.Element {
  const { name = "" } = useParams<{ name: string }>();
  const nav = useNavigate();
  const decoded = decodeURIComponent(name);
  const profileId = getActiveProfileId();

  // "rd:series:tt12345" → Real-Debrid virtual series; fetch from TMDB.
  // Tolerate an episode suffix ("rd:series:tt12345:S01E04") — cards used to
  // hand the full episode id over and landed on a dead local-catalog lookup.
  const rdMatch = /^rd:series:(tt\d+)(?::S\d{1,3}E\d{1,3})?$/.exec(decoded);
  if (rdMatch) {
    return <RdSeries imdb={rdMatch[1]!} profileId={profileId} navBack={() => nav(-1)} />;
  }

  const epsQ = useQuery({
    queryKey: ["series-episodes", decoded, profileId],
    queryFn: () => Api.library.seriesEpisodes(decoded, profileId ?? undefined),
    enabled: decoded.length > 0,
  });

  const { seasons, nextUp, hero } = useMemo(() => {
    if (!epsQ.data) return { seasons: [], nextUp: null as EpisodeRow | null, hero: null as EpisodeRow | null };
    const map = new Map<number | null, { season: number | null; episodes: EpisodeRow[] }>();
    const allOrdered: EpisodeRow[] = [];
    for (const ep of epsQ.data.items) {
      const parsed = parseEpisodeTitle(ep.title, decoded);
      const enriched: EpisodeRow = { ...ep, ...parsed };
      const bucket = map.get(parsed.season) ?? { season: parsed.season, episodes: [] };
      bucket.episodes.push(enriched);
      map.set(parsed.season, bucket);
    }
    const seasonsSorted = [...map.values()]
      .sort((a, b) => (a.season ?? Infinity) - (b.season ?? Infinity))
      .map((s) => ({
        ...s,
        episodes: s.episodes.sort((a, b) => (a.episode ?? 0) - (b.episode ?? 0)),
      }));
    for (const s of seasonsSorted) allOrdered.push(...s.episodes);

    // Next up = first in_progress episode, then first unwatched after the
    // latest-watched. Falls back to the first episode in the list.
    const inProgress = allOrdered.find((e) => statusFor(e) === "in_progress");
    let next: EpisodeRow | null = inProgress ?? null;
    if (!next) {
      const lastWatchedIdx = allOrdered.map(statusFor).lastIndexOf("watched");
      next = allOrdered.slice(lastWatchedIdx + 1).find((e) => statusFor(e) === "unwatched") ?? allOrdered.find((e) => statusFor(e) === "unwatched") ?? null;
    }
    // Hero choice: backdrop from the first enriched episode (they share one).
    const hero = allOrdered.find((e) => e.tmdb_backdrop_url) ?? allOrdered[0] ?? null;
    return { seasons: seasonsSorted, nextUp: next, hero };
  }, [epsQ.data, decoded]);

  const totalEpisodes = epsQ.data?.episode_count ?? 0;
  const overview = hero?.tmdb_overview ?? null;
  const rating = typeof hero?.tmdb_rating === "number" ? Math.round(hero.tmdb_rating * 10) / 10 : null;

  // Selected season — default to the LATEST season (highest number) so the
  // viewer lands where new episodes drop. The Resume/Play-next pill in the
  // hero still gets them to their actual next-up episode if it's in a
  // different season.
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  useEffect(() => {
    if (selectedSeason !== null || seasons.length === 0) return;
    const numbered = seasons.filter((s) => s.season !== null);
    const latest = numbered.length > 0
      ? numbered.reduce((a, b) => ((a.season ?? 0) >= (b.season ?? 0) ? a : b))
      : seasons[0]!;
    setSelectedSeason(latest.season);
  }, [seasons, selectedSeason]);
  const currentSeason = seasons.find((s) => s.season === selectedSeason) ?? seasons[0] ?? null;

  return (
    <div>
      <button onClick={() => nav(-1)} className="pill-ghost mb-4">
        <ArrowLeft size={14} /> Back
      </button>

      <header className="relative rounded-2xl border border-hairline overflow-hidden mb-8">
        <div className="h-[300px] bg-gradient-to-br from-card via-elev to-lacquer relative">
          {(hero?.tmdb_backdrop_url ?? hero?.thumbnail_url) && (
            <img
              src={hero?.tmdb_backdrop_url ?? hero?.thumbnail_url ?? ""}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "saturate(0.85) contrast(1.05)" }}
              referrerPolicy="no-referrer"
            />
          )}
          <div className="absolute inset-0" style={{
            background:
              "linear-gradient(to top, rgba(15,6,6,0.97) 0%, rgba(15,6,6,0.5) 50%, rgba(26,11,11,0.2) 100%), radial-gradient(ellipse at 8% 92%, rgba(164,32,45,0.3), transparent 50%)",
          }} />
          <div className="absolute inset-0 p-8 flex flex-col justify-end">
            <div className="section-label">Series</div>
            <h1 className="hero-title mt-2 max-w-[60ch] drop-shadow-lg">{decoded}</h1>
            <div className="hero-underline mt-4 w-72 max-w-full" />
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-parchment text-sm">
              {rating !== null && <span className="font-display text-goldHi tabular-nums">★ {rating}</span>}
              <span>{epsQ.isLoading ? "…" : `${totalEpisodes} ${totalEpisodes === 1 ? "episode" : "episodes"}`}</span>
              {seasons.length > 1 && <span>{seasons.length} seasons</span>}
            </div>
            {overview && (
              <p className="mt-3 text-parchment/85 text-sm leading-relaxed line-clamp-3 max-w-[80ch]">
                {overview}
              </p>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {nextUp && (
                <Link
                  to={`/watch/${encodeURIComponent(nextUp.id)}`}
                  className="pill bg-vermilion border-goldHi text-ivory font-display tracking-smallcaps"
                  style={{ paddingLeft: "1.5rem", paddingRight: "1.5rem" }}
                >
                  {statusFor(nextUp) === "in_progress" ? <RotateCcw size={16} /> : <Play size={16} />}
                  {statusFor(nextUp) === "in_progress"
                    ? `Resume S${nextUp.season ?? "?"}E${nextUp.episode ?? "?"}`
                    : `Play next · S${nextUp.season ?? "?"}E${nextUp.episode ?? "?"}`}
                </Link>
              )}
              <FavoriteShowButton seriesName={decoded} profileId={profileId} />
            </div>
          </div>
        </div>
      </header>

      {epsQ.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-16" />)}
        </div>
      ) : (
        currentSeason && (
          <SeasonView
            seasons={seasons}
            current={currentSeason}
            onSelect={setSelectedSeason}
            nextUpId={nextUp?.id ?? null}
          />
        )
      )}
    </div>
  );
}

function SeasonView({
  seasons,
  current,
  onSelect,
  nextUpId,
}: {
  seasons: { season: number | null; episodes: EpisodeRow[] }[];
  current: { season: number | null; episodes: EpisodeRow[] };
  onSelect: (n: number | null) => void;
  nextUpId: string | null;
}): JSX.Element {
  return (
    <section className="mb-8">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-3">
        <div className="section-label">
          {current.season === null ? "Episodes" : `Season ${current.season}`}
          <span className="text-parchment/60 ml-2 normal-case tracking-normal">
            · {current.episodes.length} {current.episodes.length === 1 ? "episode" : "episodes"}
          </span>
        </div>
        {seasons.length > 1 && (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-parchment/60 text-xs uppercase tracking-smallcaps font-display">Jump to</span>
            <select
              value={current.season ?? ""}
              onChange={(e) => onSelect(e.target.value === "" ? null : Number(e.target.value))}
              className="bg-ink/60 border border-hairline rounded-md px-3 py-1.5 text-ivory font-display tracking-smallcaps focus:border-gold outline-none"
            >
              {seasons.map((s) => (
                <option key={s.season ?? "x"} value={s.season ?? ""}>
                  {s.season === null
                    ? `Other (${s.episodes.length})`
                    : `Season ${s.season} · ${s.episodes.length} ep${s.episodes.length === 1 ? "" : "s"}`}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <ul className="space-y-1">
        {current.episodes.map((ep) => (
          <EpisodeRowCard
            key={ep.id}
            ep={ep}
            isNextUp={nextUpId === ep.id}
          />
        ))}
      </ul>
    </section>
  );
}

function EpisodeRowCard({ ep, isNextUp }: { ep: EpisodeRow; isNextUp: boolean }): JSX.Element {
  const status = statusFor(ep);
  const pct =
    status === "in_progress" && typeof ep.last_position_s === "number" && ep.duration_s && ep.duration_s > 0
      ? Math.min(100, Math.round((ep.last_position_s / ep.duration_s) * 100))
      : null;
  return (
    <li>
      <Link
        to={`/watch/${encodeURIComponent(ep.id)}`}
        className={`card flex items-center gap-4 p-3 transition-colors group ${
          isNextUp
            ? "border-goldHi/70 bg-vermilion/10 hover:bg-vermilion/20"
            : "hover:bg-elev/60"
        }`}
      >
        <div className="w-12 text-center font-display text-2xl text-parchment tabular-nums">
          {ep.episode !== null ? String(ep.episode).padStart(2, "0") : "—"}
        </div>
        <StatusBadge status={status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <div className="text-ivory truncate">
              {ep.episodeTitle || `Episode ${ep.episode ?? "?"}`}
            </div>
            {isNextUp && (
              <span className="text-goldHi text-[10px] font-display tracking-smallcaps">NEXT UP</span>
            )}
          </div>
          {ep.duration_s && ep.duration_s > 0 && (
            <div className="text-parchment text-xs mt-1 tabular-nums">
              {Math.floor(ep.duration_s / 60)}m
              {status === "in_progress" && pct !== null && (
                <span className="ml-2 text-goldHi">· {pct}% watched</span>
              )}
              {status === "watched" && <span className="ml-2 text-jade">· watched</span>}
            </div>
          )}
          {pct !== null && pct > 0 && (
            <div className="mt-1 h-1 bg-ink/70 rounded">
              <div className="h-full bg-goldHi rounded" style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
        <Play size={16} className="text-parchment opacity-0 group-hover:opacity-100 transition mr-2" />
      </Link>
    </li>
  );
}

function RdSeries({
  imdb,
  profileId,
  navBack,
}: {
  imdb: string;
  profileId: string | null;
  navBack: () => void;
}): JSX.Element {
  const q = useQuery({
    queryKey: ["series-rd-episodes", imdb, profileId],
    queryFn: () => Api.library.seriesEpisodesRd(imdb, profileId ?? undefined),
  });

  const { seasons, nextUp } = useMemo(() => {
    if (!q.data) return { seasons: [] as { season: number | null; episodes: EpisodeRow[] }[], nextUp: null as EpisodeRow | null };
    const map = new Map<number, EpisodeRow[]>();
    for (const it of q.data.items) {
      const row: EpisodeRow = {
        id: it.id,
        kind: "tv_show",
        title: it.title,
        thumbnail_url: it.thumbnail_url,
        duration_s: it.duration_s ?? null,
        category: q.data.series_name,
        creator_name: null,
        published_at: null,
        source_id: -1,
        tags: [],
        tmdb_overview: it.tmdb_overview ?? null,
        tmdb_rating: it.tmdb_rating ?? null,
        last_event: it.last_event ?? null,
        last_position_s: it.last_position_s ?? null,
        season: it.season,
        episode: it.episode,
        episodeTitle: it.episodeTitle,
      };
      const list = map.get(it.season) ?? [];
      list.push(row);
      map.set(it.season, list);
    }
    const seasonsSorted = [...map.entries()]
      .sort(([a], [b]) => a - b)
      .map(([season, episodes]) => ({
        season,
        episodes: episodes.sort((a, b) => (a.episode ?? 0) - (b.episode ?? 0)),
      }));
    const all: EpisodeRow[] = [];
    for (const s of seasonsSorted) all.push(...s.episodes);
    const inProgress = all.find((e) => statusFor(e) === "in_progress");
    let next: EpisodeRow | null = inProgress ?? null;
    if (!next) {
      const lastWatchedIdx = all.map(statusFor).lastIndexOf("watched");
      next = all.slice(lastWatchedIdx + 1).find((e) => statusFor(e) === "unwatched") ?? all.find((e) => statusFor(e) === "unwatched") ?? null;
    }
    return { seasons: seasonsSorted, nextUp: next };
  }, [q.data]);

  const data = q.data;
  const overview = data?.overview ?? null;
  const rating = typeof data?.vote_average === "number" ? Math.round(data.vote_average * 10) / 10 : null;

  // Season selector — default to the latest season.
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  useEffect(() => {
    if (selectedSeason !== null || seasons.length === 0) return;
    const numbered = seasons.filter((s) => s.season !== null);
    const latest = numbered.length > 0
      ? numbered.reduce((a, b) => ((a.season ?? 0) >= (b.season ?? 0) ? a : b))
      : seasons[0]!;
    setSelectedSeason(latest.season);
  }, [seasons, selectedSeason]);
  const currentSeason = seasons.find((s) => s.season === selectedSeason) ?? seasons[0] ?? null;

  const nav = useNavigate();
  const nowPlaying = useNowPlaying();
  const castingThisSeries = nowPlaying?.item_id?.startsWith(`rd:series:${imdb}`) ?? false;

  return (
    <div>
      <button onClick={navBack} className="pill-ghost mb-4">
        <ArrowLeft size={14} /> Back
      </button>

      {castingThisSeries && (
        <button
          onClick={() => nav(`/watch/${encodeURIComponent(nowPlaying!.item_id!)}`)}
          className="w-full mb-4 px-4 py-3 rounded-xl bg-vermilion/15 border border-vermilion/30 hover:bg-vermilion/25 transition-colors flex items-center gap-3 text-left"
        >
          <Tv2 size={16} className="text-vermilion flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-vermilion/70 uppercase tracking-widest font-display leading-none mb-0.5">Now Playing on TV</div>
            <div className="text-ivory text-sm truncate">{nowPlaying!.title ?? "Episode in progress"}</div>
          </div>
          <span className="text-parchment/60 text-xs">Return to controller →</span>
          <span className="block w-2 h-2 rounded-full bg-vermilion flex-shrink-0 animate-pulse" />
        </button>
      )}

      <header className="relative rounded-2xl border border-hairline overflow-hidden mb-8">
        <div className="h-[300px] bg-gradient-to-br from-card via-elev to-lacquer relative">
          {data?.backdrop_url && (
            <img
              src={data.backdrop_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "saturate(0.85) contrast(1.05)" }}
              referrerPolicy="no-referrer"
            />
          )}
          <div className="absolute inset-0" style={{
            background:
              "linear-gradient(to top, rgba(15,6,6,0.97) 0%, rgba(15,6,6,0.5) 50%, rgba(26,11,11,0.2) 100%), radial-gradient(ellipse at 8% 92%, rgba(164,32,45,0.3), transparent 50%)",
          }} />
          <div className="absolute inset-0 p-8 flex flex-col justify-end">
            <div className="section-label">
              Series · <span className="text-jade">via Real-Debrid</span>
            </div>
            <h1 className="hero-title mt-2 drop-shadow-lg">{data?.series_name ?? "…"}</h1>
            <div className="hero-underline mt-4 w-72 max-w-full" />
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-parchment text-sm">
              {rating !== null && <span className="font-display text-goldHi tabular-nums">★ {rating}</span>}
              <span>{data ? `${data.episode_count} episodes` : "…"}</span>
              {seasons.length > 1 && <span>{seasons.length} seasons</span>}
              {data?.first_air_date && <span>{data.first_air_date.slice(0, 4)}</span>}
              {data?.created_by && <span className="text-gold/80">Created by {data.created_by}</span>}
            </div>
            {overview && (
              <p className="mt-3 text-parchment/85 text-sm leading-relaxed line-clamp-3 max-w-[80ch]">
                {overview}
              </p>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {nextUp && (
                <Link
                  to={`/watch/${encodeURIComponent(nextUp.id)}`}
                  className="pill bg-vermilion border-goldHi text-ivory font-display tracking-smallcaps"
                  style={{ paddingLeft: "1.5rem", paddingRight: "1.5rem" }}
                >
                  {statusFor(nextUp) === "in_progress" ? <RotateCcw size={16} /> : <Play size={16} />}
                  {statusFor(nextUp) === "in_progress"
                    ? `Resume S${nextUp.season ?? "?"}E${nextUp.episode ?? "?"}`
                    : `Play next · S${nextUp.season ?? "?"}E${nextUp.episode ?? "?"}`}
                </Link>
              )}
              {data?.series_name && (
                <FavoriteShowButton seriesName={data.series_name} profileId={profileId} />
              )}
            </div>
          </div>
        </div>
      </header>

      <CastStrip cast={data?.cast ?? []} />

      {q.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-16" />)}
        </div>
      ) : (
        currentSeason && (
          <SeasonView
            seasons={seasons}
            current={currentSeason}
            onSelect={setSelectedSeason}
            nextUpId={nextUp?.id ?? null}
          />
        )
      )}
    </div>
  );
}

function FavoriteShowButton({ seriesName, profileId }: { seriesName: string; profileId: string | null }): JSX.Element | null {
  const pinId = `series:${seriesName}`;
  const qc = useQueryClient();
  const pinsQ = useQuery({
    queryKey: ["pins", profileId],
    queryFn: () => Api.library.pins(profileId!),
    enabled: !!profileId,
    staleTime: 60_000,
  });
  const isPinned = !!pinsQ.data?.items.some((p) => p.id === pinId);
  const toggle = useMutation({
    mutationFn: async () => {
      if (!profileId) return;
      if (isPinned) await Api.library.unpin(profileId, pinId);
      else await Api.library.pin({ profile_id: profileId, item_id: pinId });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["pins", profileId] });
    },
  });
  if (!profileId) return null;
  return (
    <button
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      className={`pill ${isPinned ? "bg-vermilion border-goldHi text-ivory" : "pill-ghost"}`}
      title={isPinned ? "Remove this show from Favorites" : "Favorite the whole show (not just an episode)"}
    >
      <Heart size={14} fill={isPinned ? "currentColor" : "transparent"} />
      {isPinned ? "Favorited series" : "Favorite series"}
    </button>
  );
}

function StatusBadge({ status }: { status: "watched" | "in_progress" | "unwatched" }): JSX.Element {
  if (status === "watched") {
    return (
      <div className="w-7 h-7 rounded-full bg-jade/30 border border-jade flex items-center justify-center" title="Watched">
        <Check size={14} className="text-jade" />
      </div>
    );
  }
  if (status === "in_progress") {
    return (
      <div className="w-7 h-7 rounded-full bg-vermilion/30 border border-goldHi flex items-center justify-center" title="In progress">
        <RotateCcw size={14} className="text-goldHi" />
      </div>
    );
  }
  return (
    <div className="w-7 h-7 rounded-full border border-hairline" title="Unwatched" />
  );
}
